import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Shield, Eye, Settings, Send, ToggleLeft, ToggleRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const ROLES = {
  admin:    { label: 'Administrateur', desc: 'Accès complet — créer des comptes, modifier tout', color: '#DC2626', icon: Shield },
  manager:  { label: 'Manager',        desc: 'Voir + modifier statuts + répondre aux clients', color: '#E8920A', icon: Settings },
  readonly: { label: 'Lecture seule',  desc: 'Voir les dossiers uniquement, aucune action',   color: '#6B7280', icon: Eye },
};

export default function Team() {
  const [members, setMembers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'manager', whatsapp: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const load = async () => {
    try {
      const [membersRes, meRes] = await Promise.all([
        axios.get(`${API}/api/team`),
        axios.get(`${API}/api/auth/me`)
      ]);
      setMembers(membersRes.data);
      setCurrentUser(meRes.data.admin);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/team`, form);
      setForm({ name: '', email: '', password: '', role: 'manager', whatsapp: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const toggle = async (id, active) => {
    try {
      await axios.patch(`${API}/api/team/${id}`, { active: !active });
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer le compte de ${name} ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/api/team/${id}`);
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const sendCredentials = async (id) => {
    try {
      await axios.post(`${API}/api/team/${id}/send-credentials`);
      alert('Identifiants envoyés par WhatsApp.');
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const isAdmin = currentUser?.role === 'admin' || !currentUser?.role;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Équipe</h1>
          <p style={styles.subtitle}>Gérez les accès à l'espace administration FlyCash</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(f => !f)} style={styles.addBtn}>
            <Plus size={15} /> Ajouter un membre
          </button>
        )}
      </div>

      {/* Role legend */}
      <div style={styles.legend}>
        {Object.entries(ROLES).map(([key, { label, desc, color, icon: Icon }]) => (
          <div key={key} style={styles.legendCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Icon size={14} color={color} />
              <span style={{ ...styles.roleBadge, background: `${color}18`, color }}>{label}</span>
            </div>
            <p style={styles.legendDesc}>{desc}</p>
          </div>
        ))}
      </div>

      {/* Create form */}
      {showForm && isAdmin && (
        <div style={styles.form}>
          <h3 style={styles.formTitle}>Nouveau membre</h3>
          <div style={styles.formGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Nom complet *</label>
              <input style={styles.input} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Clotaire Dupont" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email *</label>
              <input style={styles.input} type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="clotaire@flycash.eu" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Mot de passe *</label>
              <input style={styles.input} type="password" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Minimum 8 caractères" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>WhatsApp (pour alertes)</label>
              <input style={styles.input} value={form.whatsapp}
                onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
                placeholder="22670346257" />
            </div>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Rôle *</label>
              <div style={styles.roleSelect}>
                {Object.entries(ROLES).map(([key, { label, color, icon: Icon }]) => (
                  <div
                    key={key}
                    onClick={() => setForm(f => ({ ...f, role: key }))}
                    style={{
                      ...styles.roleOption,
                      borderColor: form.role === key ? color : '#E5E7EB',
                      background: form.role === key ? `${color}10` : '#fff',
                    }}
                  >
                    <Icon size={16} color={color} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: form.role === key ? color : '#374151' }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={styles.formActions}>
            <button onClick={() => setShowForm(false)} style={styles.cancelBtn}>Annuler</button>
            <button
              onClick={handleCreate}
              disabled={saving || !form.name || !form.email || !form.password}
              style={styles.saveBtn}
            >
              {saving ? 'Création...' : 'Créer le compte'}
            </button>
          </div>
        </div>
      )}

      {/* Members list */}
      {loading ? (
        <p style={styles.empty}>Chargement...</p>
      ) : (
        <div style={styles.membersList}>
          {members.map(m => {
            const role = ROLES[m.role] || ROLES.readonly;
            const RoleIcon = role.icon;
            const isMe = currentUser?.id === m.id;
            return (
              <div key={m.id} style={{ ...styles.memberCard, opacity: m.active ? 1 : 0.55 }}>
                <div style={styles.memberAvatar}>
                  {m.name[0].toUpperCase()}
                </div>
                <div style={styles.memberInfo}>
                  <div style={styles.memberName}>
                    {m.name}
                    {isMe && <span style={styles.meBadge}>Vous</span>}
                    {!m.active && <span style={styles.inactiveBadge}>Inactif</span>}
                  </div>
                  <div style={styles.memberEmail}>{m.email}</div>
                  {m.whatsapp && <div style={styles.memberWA}>+{m.whatsapp}</div>}
                  {m.last_login && (
                    <div style={styles.memberLogin}>
                      Dernière connexion : {new Date(m.last_login).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
                <div style={styles.memberRole}>
                  <span style={{ ...styles.roleBadge, background: `${role.color}18`, color: role.color }}>
                    <RoleIcon size={11} /> {role.label}
                  </span>
                  <div style={styles.memberSince}>
                    Depuis {new Date(m.created_at || Date.now()).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
                {isAdmin && !isMe && (
                  <div style={styles.memberActions}>
                    {m.whatsapp && (
                      <button onClick={() => sendCredentials(m.id)} style={styles.actionBtn} title="Envoyer les identifiants par WhatsApp">
                        <Send size={14} />
                      </button>
                    )}
                    <button onClick={() => toggle(m.id, m.active)} style={styles.actionBtn}
                      title={m.active ? 'Désactiver' : 'Activer'}>
                      {m.active
                        ? <ToggleRight size={18} color="#0D9F57" />
                        : <ToggleLeft size={18} color="#9CA3AF" />}
                    </button>
                    <button onClick={() => remove(m.id, m.name)} style={{ ...styles.actionBtn, color: '#DC2626' }} title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '24px 32px', maxWidth: 1300, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 800, color: '#0D1B3E', margin: 0 },
  subtitle: { fontSize: 13, color: '#9CA3AF', marginTop: 4 },
  addBtn: { display: 'flex', alignItems: 'center', gap: 6, background: '#0D1B3E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  legend: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 },
  legendCard: { background: '#fff', borderRadius: 10, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  legendDesc: { fontSize: 12, color: '#6B7280', margin: 0 },
  form: { background: '#fff', borderRadius: 14, padding: '24px 32px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  formTitle: { fontSize: 15, fontWeight: 800, color: '#0D1B3E', margin: '0 0 16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  roleSelect: { display: 'flex', gap: 10 },
  roleOption: { flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, border: '2px solid', cursor: 'pointer', transition: 'all 0.15s' },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: { padding: '9px 16px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', fontSize: 13, cursor: 'pointer' },
  saveBtn: { padding: '9px 16px', borderRadius: 8, background: '#0D1B3E', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  membersList: { display: 'flex', flexDirection: 'column', gap: 12 },
  memberCard: { background: '#fff', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16 },
  memberAvatar: { width: 44, height: 44, borderRadius: '50%', background: '#0D1B3E', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 },
  memberInfo: { flex: 1, minWidth: 0 },
  memberName: { fontSize: 15, fontWeight: 700, color: '#0D1B3E', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },
  meBadge: { fontSize: 10, fontWeight: 700, background: '#EEF2FF', color: '#4F46E5', padding: '2px 7px', borderRadius: 100 },
  inactiveBadge: { fontSize: 10, fontWeight: 700, background: '#FEE2E2', color: '#DC2626', padding: '2px 7px', borderRadius: 100 },
  memberEmail: { fontSize: 13, color: '#6B7280' },
  memberWA: { fontSize: 12, color: '#9CA3AF' },
  memberLogin: { fontSize: 11, color: '#D1D5DB', marginTop: 2 },
  memberRole: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 },
  roleBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100 },
  memberSince: { fontSize: 11, color: '#D1D5DB' },
  memberActions: { display: 'flex', gap: 6, flexShrink: 0 },
  actionBtn: { background: '#F3F4F6', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', display: 'flex', color: '#374151' },
  empty: { textAlign: 'center', color: '#9CA3AF', padding: 40 },
};
